import BrandLogo from '@/components/common/BrandLogo';
import { brandConfig } from '@/config/brandConfig';
import type { TemplateSender } from '@/lib/printTemplates';
import type { HandoverPage } from '@/lib/courierHandover';

export default function CourierHandoverSheets({
  pages,
  preparedAt,
  timezone,
  includePhone,
  sender,
  measurement = false,
}: {
  pages: HandoverPage[];
  preparedAt: string;
  timezone: string;
  includePhone: boolean;
  sender: TemplateSender | null;
  measurement?: boolean;
}) {
  const prepared = new Intl.DateTimeFormat('en-IN', {
    timeZone: timezone,
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(preparedAt));
  const widths = includePhone ? [6, 17, 20, 27, 16, 14] : [6, 19, 24, 33, 18];
  return (
    <div className="ho-pages" style={{ fontFamily: 'Arial, sans-serif', color: '#17202c' }}>
      <style>{`
      .ho-pages *{box-sizing:border-box}.ho-page{box-sizing:border-box;width:190mm;height:276mm;margin:0 auto 16px;padding:4mm;border:1px solid #cbd2dc;display:flex;flex-direction:column;gap:4mm;background:#fff;color:#17202c;font:10.5pt/1.4 Arial,sans-serif;print-color-adjust:exact;-webkit-print-color-adjust:exact}
      .ho-page p,.ho-page h2,.ho-page h3{margin:0}.ho-header,.ho-ack{flex-shrink:0;overflow-wrap:anywhere}.ho-brand{display:flex;align-items:flex-start;gap:3mm;border-bottom:1px solid #b99a5d;padding-bottom:3mm}.ho-brand img{width:12mm;height:12mm;flex-shrink:0;object-fit:contain}.ho-brand-name{font-size:17pt;font-weight:700;letter-spacing:.08em}.ho-tagline{font-size:9pt;color:#4b5563}.ho-doc-kind{margin-left:auto;max-width:44%;text-align:right;font-size:9pt;font-weight:700;letter-spacing:.06em;line-height:1.5}.ho-sender{margin-top:3mm;font-size:10.5pt;white-space:pre-wrap}.ho-heading{display:flex;align-items:flex-start;justify-content:space-between;gap:4mm;margin-top:3mm}.ho-heading h2{font-size:13pt;font-weight:700;line-height:1.4;overflow-wrap:anywhere}.ho-heading .ho-prepared{max-width:48%;text-align:right;font-size:9pt;color:#4b5563;line-height:1.5}.ho-body{flex:1;min-height:0}.ho-table{width:100%;table-layout:fixed;border-collapse:collapse;font:10.5pt/1.4 Arial,sans-serif;color:#17202c}.ho-table th{background:#edf1f5;border-top:1px solid #cbd2dc;border-bottom:1px solid #cbd2dc;font-size:10pt;font-weight:700;text-align:left;line-height:1.35;padding:2.2mm 1.4mm;overflow-wrap:anywhere}.ho-table td{vertical-align:top;border-bottom:1px solid #dde2e9;text-align:left;padding:2.2mm 1.4mm;overflow-wrap:anywhere;word-break:normal;white-space:normal}.ho-table tbody tr:nth-child(even){background:#f8fafc}.ho-table .ho-mono{font-family:Consolas,'Courier New',monospace;font-size:10.5pt}.ho-phone{display:block;margin-top:1mm;font-family:Consolas,'Courier New',monospace}.ho-ack{border-top:1px solid #aeb8c6;padding-top:3mm}.ho-counts{display:flex;flex-wrap:wrap;justify-content:space-between;gap:1mm 3mm;font-size:9.5pt;margin-bottom:3mm}.ho-writing-grid{display:grid;grid-template-columns:1fr 1fr;gap:2.5mm 5mm}.ho-writing{display:flex;align-items:baseline;gap:2mm;min-height:6.5mm;font-size:10.5pt;white-space:nowrap}.ho-writing span{flex:1;min-width:5mm;height:5mm;border-bottom:1px solid #687488}.ho-writing-wide{grid-column:1/-1}.ho-footer{display:flex;align-items:flex-end;justify-content:space-between;gap:4mm;margin-top:3mm;font-size:9pt;line-height:1.4;color:#4b5563}.ho-footer p{max-width:80%}.ho-footer strong{white-space:nowrap}
      .ho-page .ho-doc-kind{margin-left:auto}.ho-page .ho-sender{margin-top:3mm}
      @media print{@page{size:A4 portrait;margin:10mm}.ho-page{margin:0;break-inside:avoid;page-break-inside:avoid;break-after:page;page-break-after:always}.ho-page:last-child{break-after:auto;page-break-after:auto}.ho-table tr{break-inside:avoid;page-break-inside:avoid}}
    `}</style>
      {pages.map((page, index) => (
        <section
          key={`${page.courier}:${page.groupPage}`}
          className="ho-page"
          aria-label={`Handover sheet ${index + 1}`}
        >
          <header className="ho-header">
            <div className="ho-brand">
              <BrandLogo width={46} height={46} />
              <div>
                <p className="ho-brand-name">{brandConfig.name.toUpperCase()}</p>
                <p className="ho-tagline">{brandConfig.tagline}</p>
              </div>
              <p className="ho-doc-kind">
                Internal courier
                <br />
                handover worksheet
              </p>
            </div>
            {sender && (
              <p className="ho-sender">
                <strong>{sender.name}</strong>
                {' · '}
                {sender.address}
                {sender.phone ? ` · ${sender.phone}` : ''}
              </p>
            )}
            <div className="ho-heading">
              <div>
                <h2>Courier: {page.courier}</h2>
                <p style={{ fontSize: '9pt', color: '#4b5563' }}>
                  Group page {measurement ? 100 : page.groupPage} of{' '}
                  {measurement ? 100 : page.groupPages}
                </p>
              </div>
              <p className="ho-prepared">
                Prepared: {prepared}
                <br />
                {timezone}
              </p>
            </div>
          </header>
          <div className="ho-body">
            <table className="ho-table">
              <colgroup>
                {widths.map((width, i) => (
                  <col key={i} style={{ width: `${width}%` }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th scope="col">No.</th>
                  <th scope="col">Sale reference</th>
                  <th scope="col">Customer</th>
                  <th scope="col">Tracking / AWB</th>
                  <th scope="col">Saved stage</th>
                  {includePhone && <th scope="col">Customer phone</th>}
                </tr>
              </thead>
              <tbody>
                {page.rows.map((row, i) => (
                  <tr key={row.saleId}>
                    <td>{page.start + i + 1}</td>
                    <td>{row.saleNumber}</td>
                    <td>{row.customerName || 'Not recorded'}</td>
                    <td className="ho-mono">{row.trackingNumber}</td>
                    <td>{row.stage === 'packed' ? 'Packed' : 'Dispatched'}</td>
                    {includePhone && (
                      <td className="ho-mono">
                        <span data-customer-phone>{row.customerPhone || 'Not recorded'}</span>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <footer className="ho-ack">
            <div className="ho-counts">
              <p>
                Rows on this page: <strong>{page.rows.length}</strong>
              </p>
              <p>
                Listed shipment records in this group: <strong>{page.groupRecords}</strong>
              </p>
            </div>
            <div className="ho-writing-grid">
              <p className="ho-writing">
                Physical parcels:
                <span />
              </p>
              <p className="ho-writing">
                Handed over by:
                <span />
              </p>
              <p className="ho-writing">
                Received by:
                <span />
              </p>
              <p className="ho-writing">
                Received date/time:
                <span />
              </p>
              <p className="ho-writing ho-writing-wide">
                Signature:
                <span />
              </p>
            </div>
            <div className="ho-footer">
              <p>
                Prepared worksheet. Physical acknowledgement is completed by hand; saved stages are
                shown as prepared.
              </p>
              <strong>
                Page {measurement ? 100 : index + 1} of {measurement ? 100 : pages.length}
              </strong>
            </div>
          </footer>
        </section>
      ))}
    </div>
  );
}
